import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  Sparkles, Wand2, DollarSign, Instagram, FileSearch,
  Copy, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Sidebar from '../components/Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CopilotPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();

  // Social post state
  const [socialPrompt, setSocialPrompt] = useState('');
  const [socialPlatform, setSocialPlatform] = useState('instagram');
  const [socialResult, setSocialResult] = useState('');
  const [socialLoading, setSocialLoading] = useState(false);

  // Pricing state
  const [pricingType, setPricingType] = useState('wedding');
  const [pricingCity, setPricingCity] = useState('');
  const [pricingExperience, setPricingExperience] = useState('3');
  const [pricingResult, setPricingResult] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // SEO state
  const [seoGalleryId, setSeoGalleryId] = useState('');
  const [seoResult, setSeoResult] = useState(null);
  const [seoLoading, setSeoLoading] = useState(false);

  const [copied, setCopied] = useState(false);

  const generateSocialPost = async () => {
    if (!socialPrompt.trim()) { toast.error('Opisz co chcesz opublikowac'); return; }
    setSocialLoading(true);
    try {
      const res = await axios.post(`${API}/copilot/social-post`, {
        description: socialPrompt,
        platform: socialPlatform,
        language: language,
      });
      setSocialResult(res.data.post || res.data.result || JSON.stringify(res.data));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Blad generowania posta');
    } finally {
      setSocialLoading(false);
    }
  };

  const suggestPricing = async () => {
    setPricingLoading(true);
    try {
      const res = await axios.post(`${API}/copilot/suggest-pricing`, {
        session_type: pricingType,
        city: pricingCity,
        experience_years: parseInt(pricingExperience),
      });
      setPricingResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Blad sugestii cenowej');
    } finally {
      setPricingLoading(false);
    }
  };

  const generateSeo = async () => {
    if (!seoGalleryId.trim()) { toast.error('Wpisz ID galerii'); return; }
    setSeoLoading(true);
    try {
      const res = await axios.post(`${API}/galleries/${seoGalleryId}/generate-seo`);
      setSeoResult(res.data);
      toast.success('SEO wygenerowane');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Blad generowania SEO');
    } finally {
      setSeoLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Skopiowano');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/ai" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        <div className="mb-10">
          <h1 className="text-3xl font-bold mb-1">
            <Sparkles className="w-8 h-8 inline mr-2 text-primary" />
            {language === 'pl' ? 'AI Asystent Fotografa' : 'AI Photographer Assistant'}
          </h1>
          <p className="text-muted-foreground">{language === 'pl' ? 'Generuj posty, sugeruj ceny, optymalizuj SEO' : 'Generate posts, suggest pricing, optimize SEO'}</p>
        </div>

        <Tabs defaultValue="social" className="space-y-6">
          <TabsList className="bg-secondary">
            <TabsTrigger value="social" className="gap-2"><Instagram className="w-4 h-4" />Social Media</TabsTrigger>
            <TabsTrigger value="pricing" className="gap-2"><DollarSign className="w-4 h-4" />{language === 'pl' ? 'Cennik' : 'Pricing'}</TabsTrigger>
            <TabsTrigger value="seo" className="gap-2"><FileSearch className="w-4 h-4" />SEO</TabsTrigger>
          </TabsList>

          {/* Social Post Generator */}
          <TabsContent value="social">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Instagram className="w-5 h-5" />
                    {language === 'pl' ? 'Generator postow na social media' : 'Social Media Post Generator'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>{language === 'pl' ? 'Platforma' : 'Platform'}</Label>
                    <select
                      className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm mt-1"
                      value={socialPlatform}
                      onChange={e => setSocialPlatform(e.target.value)}
                    >
                      <option value="instagram">Instagram</option>
                      <option value="facebook">Facebook</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="tiktok">TikTok</option>
                    </select>
                  </div>
                  <div>
                    <Label>{language === 'pl' ? 'Opisz sesje / wydarzenie' : 'Describe the session / event'}</Label>
                    <Textarea
                      value={socialPrompt}
                      onChange={e => setSocialPrompt(e.target.value)}
                      placeholder={language === 'pl' ? 'Np. Sesja slubna w Krakowie, zlota godzina, plener w Ojcowskim Parku Narodowym...' : 'E.g. Wedding session in Krakow, golden hour, outdoor in National Park...'}
                      rows={4}
                    />
                  </div>
                  <Button onClick={generateSocialPost} disabled={socialLoading}>
                    {socialLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                    {language === 'pl' ? 'Generuj post' : 'Generate Post'}
                  </Button>
                  {socialResult && (
                    <div className="bg-secondary/50 rounded-lg p-4 mt-4 relative">
                      <pre className="whitespace-pre-wrap text-sm">{socialResult}</pre>
                      <Button
                        size="sm" variant="ghost"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(socialResult)}
                      >
                        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Pricing Suggestions */}
          <TabsContent value="pricing">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    {language === 'pl' ? 'Sugestie cenowe' : 'Pricing Suggestions'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>{language === 'pl' ? 'Typ sesji' : 'Session Type'}</Label>
                      <select
                        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm mt-1"
                        value={pricingType}
                        onChange={e => setPricingType(e.target.value)}
                      >
                        <option value="wedding">Wesele</option>
                        <option value="portrait">Portret</option>
                        <option value="family">Rodzinna</option>
                        <option value="newborn">Noworodkowa</option>
                        <option value="corporate">Korporacyjna</option>
                        <option value="event">Wydarzenie</option>
                        <option value="product">Produktowa</option>
                      </select>
                    </div>
                    <div>
                      <Label>{language === 'pl' ? 'Miasto' : 'City'}</Label>
                      <Input value={pricingCity} onChange={e => setPricingCity(e.target.value)} placeholder="Warszawa" />
                    </div>
                    <div>
                      <Label>{language === 'pl' ? 'Lata doswiadczenia' : 'Years of Experience'}</Label>
                      <Input type="number" value={pricingExperience} onChange={e => setPricingExperience(e.target.value)} min="0" max="30" />
                    </div>
                  </div>
                  <Button onClick={suggestPricing} disabled={pricingLoading}>
                    {pricingLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                    {language === 'pl' ? 'Zasugeruj ceny' : 'Suggest Pricing'}
                  </Button>
                  {pricingResult && (
                    <div className="bg-secondary/50 rounded-lg p-4 mt-4">
                      <h3 className="font-semibold mb-3">{language === 'pl' ? 'Sugerowany cennik' : 'Suggested Pricing'}</h3>
                      {pricingResult.suggestions ? (
                        <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(pricingResult.suggestions, null, 2)}</pre>
                      ) : (
                        <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(pricingResult, null, 2)}</pre>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* SEO Generator */}
          <TabsContent value="seo">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSearch className="w-5 h-5" />
                    {language === 'pl' ? 'Generator SEO dla galerii' : 'Gallery SEO Generator'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {language === 'pl'
                      ? 'AI wygeneruje meta title, description i alt-text dla zdjec w galerii. Poprawi widocznosc w Google.'
                      : 'AI will generate meta title, description and alt-text for photos in the gallery. Improves Google visibility.'}
                  </p>
                  <div>
                    <Label>Gallery ID</Label>
                    <Input value={seoGalleryId} onChange={e => setSeoGalleryId(e.target.value)} placeholder="ID galerii z listy galerii" />
                  </div>
                  <Button onClick={generateSeo} disabled={seoLoading}>
                    {seoLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                    {language === 'pl' ? 'Generuj SEO' : 'Generate SEO'}
                  </Button>
                  {seoResult && (
                    <div className="bg-secondary/50 rounded-lg p-4 mt-4 space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Meta Title</p>
                        <p className="font-medium">{seoResult.seo?.meta_title || seoResult.meta_title}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Meta Description</p>
                        <p className="text-sm">{seoResult.seo?.meta_description || seoResult.meta_description}</p>
                      </div>
                      {(seoResult.seo?.alt_texts || seoResult.alt_texts) && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Alt Texts ({(seoResult.seo?.alt_texts || seoResult.alt_texts).length})</p>
                          <ul className="text-xs space-y-1">
                            {(seoResult.seo?.alt_texts || seoResult.alt_texts).slice(0, 5).map((alt, i) => (
                              <li key={i} className="text-muted-foreground">- {alt}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
